import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'react-use';

import { type GeneralSearchResults } from '@app/client/common/structs/globalSearch/global-search';
import { callGetGlobalSearchResults } from '@app/client/resources/global-search';

const DEFAULT_DATA: GeneralSearchResults = {
    collectibles: [],
    collections: [],
    freeYats: [],
    users: [],
    wishlists: [],
};

const MAX_LIMIT = 5;

function resultsCount(data: GeneralSearchResults) {
    return Object.values(data).reduce<number>((total, current) => total + current.length, 0);
}

function dataIsEmpty(data: GeneralSearchResults) {
    return Object.values(data).filter((value) => value.length).length === 0;
}

function assertHasData(data?: GeneralSearchResults): data is GeneralSearchResults {
    return Boolean(data !== undefined && !dataIsEmpty(data));
}

function getTrimmedResult(data: GeneralSearchResults): GeneralSearchResults {
    const result = { ...DEFAULT_DATA };
    Object.keys(data).forEach((key) => {
        result[key] = data[key].slice(0, MAX_LIMIT);
    });
    return result;
}

export const useGetGlobalSearchResults = (searchQuery: string) => {
    const [query, setQuery] = useState('');

    useDebounce(() => setQuery(searchQuery), 100, [searchQuery]);

    const cache = ['searchQuery', query];
    const response = useQuery(
        cache,
        ({ signal }) => callGetGlobalSearchResults({ settings: { signal }, searchQuery: query }),
        {
            select: (data) => {
                if (data?.ok && data.value) {
                    return data.value;
                }
            },
            retry: false,
            enabled: Boolean(searchQuery),
        },
    );

    const data = assertHasData(response.data) ? getTrimmedResult(response.data) : DEFAULT_DATA;
    const noData = !assertHasData(response.data);

    return {
        ...response,
        data,
        noData,
        totalResults: resultsCount(data),
        isError: response.isError || (!response.isLoading && noData),
    };
};
